'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { PageForm, type PageFormRef } from '@/components/partials/pages'
import { ContentError, FormSkeleton } from '@/components/partials'
import { usePage, useUpdatePage } from '@/hooks'
import { type CreatePageParams } from '@/types/page'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const pageFormRef = useRef<PageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: page, isLoading, error } = usePage(id)
  const { mutate: updatePage, isPending } = useUpdatePage()

  const handleSubmit = (data: CreatePageParams) => {
    updatePage(
      { id, params: data },
      {
        onSuccess: async updatedPage => {
          setIsRedirecting(true)
          queryClient.setQueryData(['page', id], updatedPage)
          await queryClient.invalidateQueries({ queryKey: ['pages'] })
          toast.success(t('updated_successfully'))
          router.push(`/pages/${updatedPage.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (pageFormRef.current) {
        pageFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/pages/${id}`)
  }

  if (isLoading || !page) {
    return <FormSkeleton fieldRows={1} showPictures showFiles />
  }

  if (error || !page) {
    return <ContentError error={error} listUrl="/pages" />
  }

  return (
    <PageForm
      ref={pageFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={page}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
